import React, { CSSProperties, useContext, useEffect, useMemo, useState } from 'react';
import { GameProvider } from 'contexts/GameContext';
import { GlobalProvider } from 'contexts/GlobalContext';
import { GridModeProvider } from 'contexts/GridModeContext';
import { OrientationOverrideProvider } from 'contexts/OrientationOverrideContext';
import { ShowAllDealersContext } from 'contexts/ShowAllDealersContext';
import { TableIdProvider } from 'contexts/TableIdContext';
import TableSettingsContext, {
  TABLE_VIEW_SCALE_MAX,
  TABLE_VIEW_SCALE_MIN,
  TABLE_VIEW_SCALE_STEP
} from 'contexts/TableSettingsContext';
import DemoVoteContext, { DemoVoteHandler } from 'demo/DemoVoteContext';
import {
  applyVoteStep,
  castVote,
  DeclineCase,
  isRunOutStep,
  stepAfterVote,
  VoteOffer,
  voteSeatModes,
  VoteStep
} from 'demo/multiRunVoteDemo';
import {
  BoardStage,
  buildTableDemo,
  Currency,
  DEFAULT_DEMO_BET_AMOUNT,
  DEFAULT_SEAT_KNOCKOUTS,
  DEMO_BET_AMOUNTS,
  GameVariant,
  getDemoSeatLayout,
  MAX_DEMO_KNOCKOUTS,
  MAX_DEMO_SEATS,
  MAX_DEMO_SIDE_POTS,
  MULTI_RUN_COUNTS,
  MULTI_RUN_TRIGGERS,
  MultiRunCount,
  MultiRunTrigger,
  NINE_SEAT_DEMO_POV_GUID,
  PotScenario,
  SeatActionMode,
  SeatCardMode,
  withDemoPokerActions
} from 'demo/nineSeatTableDemo';
import { useAppDispatch, useAppSelector } from 'hooks';
import { CHROME_INSET_VAR, useChromeInset } from 'hooks/useChromeInset';
import { useIsNarrowTableView } from 'hooks/useIsNarrowTableView';
import { useOrientation } from 'hooks/useOrientation';
import { useSupportsHover } from 'hooks/useSupportsHover';
import { useViewportWidth } from 'hooks/useViewportWidth';
import { gridCellStyle, gridFeltStyle, gridFooterStyle } from 'pages/gameGridLayout';
import { footerRow } from 'pages/gamePageLayout';
import { updateCurrentPlayerGUID, updateCurrentTable } from 'reducers/currentTable';
import { removeTable } from 'reducers/tables';
import { patchPlayerProfile } from 'reducers/user';
import { MAX_OPEN_TABLES } from 'utils/tableCapacity';
import { TABLE_GAP_PX, tablesThatFit } from 'utils/tableViewWidth';

import { cellStyle, gridStyle } from 'components/MultiTableGrid/MultiTableGrid';
import { adaptActiveTables } from 'components/SaloonLobby/useActiveTables';
import YourTables from 'components/SaloonLobby/YourTables';
import { actionColors } from 'components/Seat/components';
import Table from 'components/Table';
import { COMPACT_HEADER_MIN_HEIGHT } from 'components/TableHeader/TableHeader';
import TableSwitcher from 'components/TableHeader/TableSwitcher';
import UserActions from 'components/UserActions';
import MultiRunActions from 'components/UserActions/MultiRunActions';
import { offeredRunCounts } from 'components/UserActions/MultiRunActions/multiRunPhase';

const POT_SCENARIOS: PotScenario[] = ['main', 'side', 'showdown', 'winnings'];
const CURRENCIES: Currency[] = ['dollars', 'cents'];
const BOARD_STAGES: BoardStage[] = ['preflop', 'flop', 'turn', 'river'];
// Display label → variant value for the Game control.
const GAME_VARIANTS: { label: string; value: GameVariant }[] = [
  { label: 'Texas', value: 'texas' },
  { label: 'Omaha', value: 'omaha' }
];

// Run-it-twice trigger labels — the street the all-in was declared on, which
// decides how much of the board every run shares.
const MULTI_RUN_TRIGGER_LABEL: Record<MultiRunTrigger, string> = {
  preflop: 'Pre-flop',
  flop: 'Post-flop',
  turn: 'Post-turn'
};

// EN-10856 vote scenarios, in the order a hand can pass through them. Each is
// one click; the RUN IT buttons then play the table forward from the two steps
// where the decision is yours.
const VOTE_STEPS: { step: VoteStep; label: string; title: string }[] = [
  {
    step: 'you-choose',
    label: 'You choose',
    title: 'You went all in first and choose 1X / 2X / 3X'
  },
  { step: 'you-offered', label: 'You offered', title: 'You offered — the others are asked' },
  { step: 'they-choose', label: 'They choose', title: 'They went all in first and are deciding' },
  {
    step: 'they-offered',
    label: 'They offered',
    title: 'They offered — you answer 1X or the offer'
  },
  { step: 'you-agreed', label: 'You agreed', title: 'You agreed — waiting on the others' },
  { step: 'you-folded', label: 'Folded', title: 'You folded earlier and are watching the vote' },
  {
    step: 'declined',
    label: 'Ran once',
    title: 'Declined or timed out — it runs once (RUN IT 1X)'
  },
  { step: 'agreed', label: 'Agreed', title: 'Unanimous — the boards run out' }
];

// Tapping a seat in the Cards control cycles through these in order. The POV
// only cycles meaningful states (your own cards are always face-up to you).
const OPPONENT_CARD_CYCLE: SeatCardMode[] = ['down', 'show', 'fold', 'none'];
const POV_CARD_CYCLE: SeatCardMode[] = ['show', 'fold', 'none'];

// Effective card mode shown for a seat (mirrors the demo's own resolution).
const resolveSeatCardMode = (mode: SeatCardMode | undefined, isPov: boolean): SeatCardMode => {
  if (isPov) return mode === 'fold' || mode === 'none' ? mode : 'show';
  return mode ?? 'down';
};

// Button colour per card mode (also used for the legend swatches).
const CARD_MODE_COLOR: Record<SeatCardMode, string> = {
  down: 'rgba(80, 140, 220, 0.85)',
  show: 'rgba(70, 200, 120, 0.85)',
  fold: 'rgba(220, 90, 90, 0.85)',
  none: 'rgba(255, 255, 255, 0.12)'
};

const CARD_MODE_LABEL: Record<SeatCardMode, string> = {
  down: 'Face-down',
  show: 'Shown',
  fold: 'Folded',
  none: 'No cards'
};

// Tapping a seat in the Action control cycles its badge through these in order.
const SEAT_ACTION_CYCLE: SeatActionMode[] = ['none', 'check', 'call', 'bet', 'raise', 'allin'];

/** Compact button label for a bet preset: 100 → "100", 1_000_000 → "1M". */
const formatBetPreset = (amount: number): string =>
  amount >= 1_000_000
    ? `${amount / 1_000_000}M`
    : amount >= 1_000
    ? `${amount / 1_000}K`
    : String(amount);

// Short button label per action (the full label is in the tooltip).
const SEAT_ACTION_SHORT: Record<SeatActionMode, string> = {
  none: '–',
  check: 'X',
  call: 'C',
  bet: 'B',
  raise: 'R',
  allin: 'A'
};

/** Everything the controls configure for ONE demo table. */
export type DemoTableConfig = {
  seatCount: number;
  potScenario: PotScenario;
  sidePotCount: number;
  currency: Currency;
  openSeats: number;
  povSeat: number;
  povAssigned: boolean;
  seatCardModes: Record<number, SeatCardMode>;
  boardStage: BoardStage;
  bountyOn: boolean;
  seatKnockouts: Record<number, number>;
  variant: GameVariant;
  straddleSeat: number;
  seatActions: Record<number, SeatActionMode>;
  /** Seat whose turn it is; 0 = the demo's default (an opponent). */
  turnSeat: number;
  /** Chips every active seat has in front of it (before the currency scale). */
  betAmount: number;
  // Run-it-twice: how many boards, the street the all-in happened on, and how
  // far through the run-out we are (1..runs = that run is on the felt,
  // runs + 1 = every board has been paid out).
  multiRunCount: MultiRunCount;
  multiRunTrigger: MultiRunTrigger;
  multiRunProgress: number;
  // EN-10856: the vote scenario on the felt ('off' = none), the offer it
  // carries, how a run-once ended, and when its window opened (seconds, like
  // the table's own clock) so each step starts the countdown afresh.
  voteStep: VoteStep;
  voteOffer: VoteOffer;
  voteDecline: DeclineCase;
  voteStartedAt: number;
  /**
   * Bumped whenever a vote window (re)opens. It keys this table's
   * GameProvider: the panel's countdown is anchored on mount
   * (useVoteCountdown), and the vote a player casts lives in GameContext as
   * `playerMultiRunVote`, so a new window needs a new provider — remounting
   * only the panel left the previous answer behind, rendering the
   * already-voted message with the vote chips disabled.
   */
  voteNonce: number;
};

// Exported for unit testing.
export const DEFAULT_TABLE_CONFIG: DemoTableConfig = {
  seatCount: MAX_DEMO_SEATS,
  potScenario: 'main',
  sidePotCount: 2,
  currency: 'dollars',
  openSeats: 0,
  povSeat: 1,
  povAssigned: true,
  seatCardModes: {},
  boardStage: 'river',
  bountyOn: false,
  seatKnockouts: {},
  variant: 'texas',
  straddleSeat: 0,
  seatActions: {},
  turnSeat: 0,
  betAmount: DEFAULT_DEMO_BET_AMOUNT,
  multiRunCount: 0,
  multiRunTrigger: 'flop',
  multiRunProgress: 1,
  voteStep: 'off',
  voteOffer: 2,
  voteDecline: 'third-declined',
  // Stamped by every vote step before it is read.
  voteStartedAt: 0,
  voteNonce: 0
};

// Stable gameIDs for the multi-table row — the state.tables keys each cell's
// TableIdProvider reads (same scheme as the Multi-Table Grid story).
const DEMO_GAME_IDS = Array.from({ length: MAX_OPEN_TABLES }, (_, i) => String(90001 + i));

// A vote needs you and two opponents in the hand. Exported for unit testing.
export const voteCastFor = (c: DemoTableConfig) => {
  const { occupiedIds, pov } = getDemoSeatLayout(
    c.seatCount,
    c.openSeats,
    c.povSeat,
    c.povAssigned
  );
  return c.povAssigned ? castVote(occupiedIds, pov) : null;
};

/**
 * The table snapshot a config describes. A vote scenario folds everyone
 * outside the hand and takes over the multi-run state: the all-in street
 * during the window, the run-out once agreed. Nothing it does is stored, so
 * switching it off restores the rest. withDemoPokerActions runs last so the
 * real action bar gets the POV's options for the table as it finally stands
 * (a vote window leaves nobody on a betting turn). Exported for unit testing.
 */
export const buildTableFromConfig = (c: DemoTableConfig) => {
  const { occupiedIds } = getDemoSeatLayout(c.seatCount, c.openSeats, c.povSeat, c.povAssigned);
  const cast = c.voteStep !== 'off' ? voteCastFor(c) : null;
  const built = buildTableDemo(
    c.seatCount,
    c.potScenario,
    c.sidePotCount,
    c.currency,
    c.openSeats,
    c.povSeat,
    c.povAssigned,
    cast ? voteSeatModes(occupiedIds, cast, c.voteStep, c.seatCardModes) : c.seatCardModes,
    c.boardStage,
    c.bountyOn,
    c.seatKnockouts,
    c.variant,
    c.straddleSeat,
    cast
      ? {
          runs: c.voteOffer,
          trigger: c.multiRunTrigger,
          progress: isRunOutStep(c.voteStep) ? 1 : 0
        }
      : { runs: c.multiRunCount, trigger: c.multiRunTrigger, progress: c.multiRunProgress },
    c.seatActions,
    c.turnSeat,
    c.betAmount
  );
  return withDemoPokerActions(
    cast
      ? applyVoteStep(built, c.voteStep, {
          cast,
          offer: c.voteOffer,
          startedAt: c.voteStartedAt,
          hand: c.voteNonce,
          declined: c.voteDecline
        })
      : built
  );
};

/**
 * The run counts a table's footer offers. With a vote scenario on: whatever
 * the live rules offer YOU on the table as it stands (offeredRunCounts, the
 * same rule UserActions applies). Otherwise the Run-out control's inert
 * initiator row while its "Voting" step is selected. Empty = no vote panel.
 * Exported for unit testing.
 */
export const footerRunCountsFor = (c: DemoTableConfig, game: Record<string, any>): number[] => {
  if (c.voteStep !== 'off' && voteCastFor(c)) {
    const { pov } = getDemoSeatLayout(c.seatCount, c.openSeats, c.povSeat, c.povAssigned);
    return offeredRunCounts(game as any, pov);
  }
  return c.multiRunCount > 0 && c.multiRunProgress === 0 ? [1, 2, 3] : [];
};

type ControlSectionProps = {
  title: string;
  /** Scope note shown beside the title (e.g. which table it edits). */
  hint?: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
};

/** A collapsible group of control rows in the flyout. */
const ControlSection = ({ title, hint, open, onToggle, children }: ControlSectionProps) => (
  <section style={sectionStyle}>
    <button onClick={onToggle} aria-expanded={open} style={sectionHeaderStyle}>
      <span style={{ width: 12 }}>{open ? '▾' : '▸'}</span>
      <span>{title}</span>
      {hint && <span style={sectionHintStyle}>{hint}</span>}
    </button>
    {open && <div style={sectionBodyStyle}>{children}</div>}
  </section>
);

// React 18 has no typing for `inert`; an empty string sets the attribute.
const INERT_ATTR = { inert: '' } as Record<string, string>;

/**
 * The real action bar, locked. It renders so the footer takes exactly the
 * in-game size, but the demo has no server, so nothing in it may be used:
 *  - a disabled fieldset disables every native control (buttons, checkboxes);
 *    `display: contents` keeps it out of the footer's flex layout, so the
 *    size is untouched;
 *  - `inert` blocks keyboard focus and any non-native click targets;
 *  - `pointer-events: none` is the backstop for everything else.
 * The dimming is purely visual, so the bar reads as disabled (!important:
 * the action buttons set their own inline opacity).
 */
const LockedActionBar = () => (
  <fieldset disabled data-demo-locked {...INERT_ATTR} style={lockedActionBarStyle}>
    <style>{'fieldset[data-demo-locked] :is(button, input) { opacity: 0.45 !important; }'}</style>
    <UserActions />
  </fieldset>
);

type DemoActionBarProps = {
  runCounts: number[];
  /** Plays a RUN IT tap forward; null outside a vote scenario. */
  onVote: DemoVoteHandler | null;
};

/**
 * A table's footer. While a vote window is open the real vote panel fills it —
 * the same component UserActions mounts live — and stays usable: a tap plays
 * the table forward through DemoVoteContext instead of the (absent) socket.
 * The rest of the time it is the locked real action bar.
 */
const DemoActionBar = ({ runCounts, onVote }: DemoActionBarProps) =>
  runCounts.length ? (
    <DemoVoteContext.Provider value={onVote}>
      <MultiRunActions runCounts={runCounts} />
    </DemoVoteContext.Provider>
  ) : (
    <LockedActionBar />
  );

/**
 * Standalone preview route for the seat/bet layout work — no sockets, no
 * lobby, no join flow. It loads the demo data straight into Redux and renders
 * the <Table />, which follows the real viewport orientation.
 *
 * With Tables > 1 it previews multi-table: each table is seeded into its own
 * `state.tables` slot (routed by `meta.gameID`, like a live subscription) and
 * rendered in a row with the grid's cell sizing and per-cell contexts. It
 * mounts <Table /> rather than the grid's GameInner, so there are still no
 * sockets. The controls edit the table picked under "Edit"; every table keeps
 * its own multi-run / vote scenario and answers its own RUN IT taps.
 *
 * Visit /demo-table to view it.
 */
const DemoTable = () => {
  const dispatch = useAppDispatch();
  const [showAllDealers, setShowAllDealers] = useState<boolean>(true);
  const [controlsOpen, setControlsOpen] = useState<boolean>(true);
  // One config per open table; `editIndex` is the table the controls edit.
  const [tableConfigs, setTableConfigs] = useState<DemoTableConfig[]>([DEFAULT_TABLE_CONFIG]);
  const [editIndex, setEditIndex] = useState<number>(0);
  // Collapsed control sections — kept here (not in each section) so they stay
  // collapsed when the flyout is closed and reopened.
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const toggleSection = (id: string) =>
    setCollapsedSections((prev) => ({ ...prev, [id]: !prev[id] }));
  const tableCount = tableConfigs.length;
  const editConfig = tableConfigs[editIndex];
  const {
    seatCount,
    potScenario,
    sidePotCount,
    currency,
    openSeats,
    povSeat,
    povAssigned,
    seatCardModes,
    boardStage,
    bountyOn,
    seatKnockouts,
    variant,
    straddleSeat,
    seatActions,
    turnSeat,
    betAmount,
    multiRunCount,
    multiRunTrigger,
    multiRunProgress,
    voteStep,
    voteOffer
  } = editConfig;
  const isMultiRun = multiRunCount > 0;
  const isVoting = isMultiRun && multiRunProgress === 0;
  const viewportWidth = useViewportWidth();
  const supportsHover = useSupportsHover();
  const isNarrowViewport = useIsNarrowTableView();
  const orientation = useOrientation();
  const attachChromeInset = useChromeInset();
  const demoTables = useAppSelector((state) => (state as any).tables) as Record<string, any>;
  // "Table View Size" lives in TableSettingsContext (persisted); the demo drives
  // it so the scaling can be visualized live.
  const tableSettings = useContext(TableSettingsContext);

  // Which seats are occupied / the POV — so the Cards control can disable the
  // POV (always its own face-up cards) and any open seats.
  const { occupiedIds, pov } = getDemoSeatLayout(seatCount, openSeats, povSeat, povAssigned);
  const occupiedSet = new Set(occupiedIds);

  const voteCast = voteCastFor(editConfig);
  const voteOn = voteStep !== 'off' && voteCast !== null;

  // Patch the config of one table / the table being edited.
  const updateConfigAt = (index: number, patch: (c: DemoTableConfig) => Partial<DemoTableConfig>) =>
    setTableConfigs((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch(c) } : c)));
  const updateConfig = (patch: (c: DemoTableConfig) => Partial<DemoTableConfig>) =>
    updateConfigAt(editIndex, patch);

  const setSeatCount = (value: number) => updateConfig(() => ({ seatCount: value }));
  const setPotScenario = (value: PotScenario) => updateConfig(() => ({ potScenario: value }));
  const setSidePotCount = (value: number) => updateConfig(() => ({ sidePotCount: value }));
  const setCurrency = (value: Currency) => updateConfig(() => ({ currency: value }));
  const setOpenSeats = (value: number) => updateConfig(() => ({ openSeats: value }));
  const setPovSeat = (value: number) => updateConfig(() => ({ povSeat: value }));
  const setPovAssigned = (value: boolean) => updateConfig(() => ({ povAssigned: value }));
  const setBoardStage = (value: BoardStage) => updateConfig(() => ({ boardStage: value }));
  const toggleBounty = () => updateConfig((c) => ({ bountyOn: !c.bountyOn }));
  const setVariant = (value: GameVariant) => updateConfig(() => ({ variant: value }));
  const setStraddleSeat = (value: number) => updateConfig(() => ({ straddleSeat: value }));
  const setTurnSeat = (value: number) => updateConfig(() => ({ turnSeat: value }));
  const setBetAmount = (value: number) => updateConfig(() => ({ betAmount: value }));
  const setMultiRunTrigger = (value: MultiRunTrigger) =>
    updateConfig(() => ({ multiRunTrigger: value }));
  const setMultiRunProgress = (value: number) => updateConfig(() => ({ multiRunProgress: value }));

  // Every board is on the felt from the first step (they are pre-dealt live),
  // so the count alone decides how many rows the fork has. Turning multi-run ON
  // lands on the fully run-out state (every board dealt and paid) because
  // that's the layout worth previewing — the Run-out control steps back through
  // how it got there. Changing count while already on only clamps the step.
  const setMultiRunCount = (n: MultiRunCount) =>
    updateConfig((c) => ({
      multiRunCount: n,
      multiRunProgress: c.multiRunCount > 0 ? Math.min(c.multiRunProgress, n + 1) : n + 1
    }));

  // Opening the vote window again restarts its countdown (see voteNonce).
  const openVotingWindow = () =>
    updateConfig((c) => ({ multiRunProgress: 0, voteNonce: c.voteNonce + 1 }));

  const showVoteStepAt = (
    index: number,
    step: VoteStep,
    patch: { offer?: VoteOffer; declined?: DeclineCase } = {}
  ) =>
    updateConfigAt(index, (c) => ({
      voteStep: step,
      voteOffer: patch.offer ?? c.voteOffer,
      voteDecline: patch.declined ?? c.voteDecline,
      voteStartedAt: Math.floor(Date.now() / 1000),
      voteNonce: c.voteNonce + 1
    }));
  const showVoteStep = (step: VoteStep, patch?: { offer?: VoteOffer; declined?: DeclineCase }) =>
    showVoteStepAt(editIndex, step, patch);

  // A RUN IT tap on table `index`: the answer a live table would broadcast next.
  const demoVoteHandlerFor = (index: number): DemoVoteHandler | null => {
    const config = tableConfigs[index];
    if (config.voteStep === 'off' || !voteCastFor(config)) return null;
    return (vote: string) => {
      const next = stepAfterVote(config.voteStep, vote === '-2' ? 1 : Number(vote));
      if (next) showVoteStepAt(index, next.step, next);
    };
  };

  // Grow by cloning the table being edited (so a new table starts from what's
  // on screen); shrink by dropping from the end, keeping the edit index valid.
  const setTableCount = (count: number) => {
    setTableConfigs((prev) =>
      count > prev.length
        ? [...prev, ...Array.from({ length: count - prev.length }, () => prev[editIndex])]
        : prev.slice(0, count)
    );
    setEditIndex((i) => Math.min(i, count - 1));
  };

  // Per-table sections say which table they edit once there is more than one.
  const editHint = tableCount > 1 ? `Editing T${editIndex + 1}` : undefined;

  const copyConfigToAllTables = () => setTableConfigs((prev) => prev.map(() => prev[editIndex]));

  const cycleSeatCardMode = (seat: number, isPov: boolean) =>
    updateConfig((c) => {
      const cycle = isPov ? POV_CARD_CYCLE : OPPONENT_CARD_CYCLE;
      const current = resolveSeatCardMode(c.seatCardModes[seat], isPov);
      // indexOf may be -1 if the stored mode isn't in this seat's cycle (e.g. a
      // leftover 'down' on a seat that just became the POV) → start at the first.
      const next = cycle[(cycle.indexOf(current) + 1) % cycle.length];
      return { seatCardModes: { ...c.seatCardModes, [seat]: next } };
    });

  const cycleSeatKnockouts = (seat: number) =>
    updateConfig((c) => {
      const current = c.seatKnockouts[seat] ?? DEFAULT_SEAT_KNOCKOUTS;
      // Cycle 0..MAX (0 = no badge for that seat).
      const next = (current + 1) % (MAX_DEMO_KNOCKOUTS + 1);
      return { seatKnockouts: { ...c.seatKnockouts, [seat]: next } };
    });

  const cycleSeatAction = (seat: number) =>
    updateConfig((c) => {
      const current = c.seatActions[seat] ?? 'none';
      const next =
        SEAT_ACTION_CYCLE[(SEAT_ACTION_CYCLE.indexOf(current) + 1) % SEAT_ACTION_CYCLE.length];
      return { seatActions: { ...c.seatActions, [seat]: next } };
    });

  const setAllSeatActions = (action: SeatActionMode) =>
    updateConfig(() => ({
      seatActions: Object.fromEntries(occupiedIds.map((id) => [id, action]))
    }));

  // One snapshot per table; the effect loads them and the footers read them.
  const frames = useMemo(() => tableConfigs.map(buildTableFromConfig), [tableConfigs]);

  useEffect(() => {
    // Seat 1 is the POV: set the user GUID (Table reads it to flip the POV
    // seat face-up) and the current-player GUID (the reducer matches it to
    // derive seatId) BEFORE loading the table snapshot. Re-runs whenever any
    // control changes so the tables rebuild.
    dispatch(patchPlayerProfile({ playerGUID: NINE_SEAT_DEMO_POV_GUID }));
    dispatch(updateCurrentPlayerGUID(NINE_SEAT_DEMO_POV_GUID));
    // `meta.gameID` routes each payload into its own state.tables slot —
    // exactly how a live subscription arrives (see reducers/tables.ts) — and
    // each felt's TableIdProvider reads it back. The header strip is built from
    // the same slots, so closed tables are removed rather than left behind.
    frames.forEach((table, i) => {
      const gameID = DEMO_GAME_IDS[i];
      dispatch({
        ...updateCurrentTable({ ...table, game: { ...table.game, gameID: Number(gameID) } }),
        meta: { gameID }
      });
    });
    DEMO_GAME_IDS.slice(frames.length).forEach((gameID) => dispatch(removeTable(gameID)));
  }, [dispatch, frames]);

  const actionBarFor = (index: number) => (
    <DemoActionBar
      runCounts={footerRunCountsFor(tableConfigs[index], frames[index].game)}
      onVote={demoVoteHandlerFor(index)}
    />
  );

  // Mirrors TableHeader: phones and narrow windows get the table strip as their
  // header, and the grid is desktop-only (MultiTableGrid hands off below two
  // tables' width), so those widths always show one felt.
  const useStripNav = !supportsHover || isNarrowViewport;
  const showGrid = tableCount > 1 && !useStripNav;
  const editGameID = DEMO_GAME_IDS[editIndex];
  // The real strip, fed from state.tables the way useActiveTables does.
  const stripTables = adaptActiveTables([], demoTables, () => false);
  const openStripTable = (gameID: string) => {
    const index = DEMO_GAME_IDS.indexOf(gameID);
    if (index >= 0) setEditIndex(index);
  };
  const addStripTable =
    tableCount < MAX_OPEN_TABLES ? () => setTableCount(tableCount + 1) : undefined;

  // Single-table header. The felt reserves this band's MEASURED height through
  // the production hook (useChromeInset → --table-chrome-inset), exactly as the
  // real header's band does:
  //  - strip nav (phone / narrow): the real "Your Tables" strip (or the stacked
  //    badge), which is content-sized — so its height is measured, not assumed;
  //  - desktop: the dock band, a fixed COMPACT_HEADER_MIN_HEIGHT.
  const singleHeader = useStripNav ? (
    <div
      ref={attachChromeInset}
      style={
        tableSettings.stackTables
          ? stackedBadgeBandStyle
          : { ...stripBandStyle, ...(orientation === 'portrait' ? stripInsetPortrait : null) }
      }>
      {tableSettings.stackTables ? (
        <TableSwitcher
          tables={stripTables}
          onOpenTable={openStripTable}
          activeGameID={editGameID}
          onAddTable={addStripTable}
        />
      ) : (
        <YourTables
          tables={stripTables}
          onOpenTable={openStripTable}
          activeGameID={editGameID}
          bare
          autoCenter={false}
          stripLayout
          stripCapacity={MAX_OPEN_TABLES}
          onAddTable={addStripTable}
        />
      )}
    </div>
  ) : (
    <div ref={attachChromeInset} style={singleHeaderPlaceholderStyle}>
      header
    </div>
  );

  // Same sizing rule as MultiTableGrid: as many tables share the row as fit at
  // the 390px floor (capped at the open count); the rest scroll sideways.
  const tablesAcross = Math.max(1, Math.min(tableCount, tablesThatFit(viewportWidth)));
  const cellBasis = `calc((100% - ${(tablesAcross - 1) * TABLE_GAP_PX}px) / ${tablesAcross})`;
  // Each cell mirrors a grid cell's context stack (grid mode, portrait, its own
  // table id) so grid-aware components size themselves as in the real grid.
  const multiTable = (
    <div style={gridStyle}>
      {DEMO_GAME_IDS.slice(0, tableCount).map((gameID, i) => (
        <div
          key={gameID}
          style={{
            ...cellStyle,
            flexBasis: cellBasis,
            // Outline the table the controls edit (demo-only chrome).
            outline: controlsOpen && i === editIndex ? editOutline : undefined
          }}
          // Clicking a table selects it for editing, like picking it under Edit.
          onClick={() => setEditIndex(i)}>
          <ShowAllDealersContext.Provider value={showAllDealers}>
            {/* Per-cell GlobalProvider, as in MultiTableGrid: join/buy-in
                state belongs to this table, not the whole page. */}
            <GlobalProvider>
              <GridModeProvider value={true}>
                <OrientationOverrideProvider orientation='portrait'>
                  <TableIdProvider gameID={gameID}>
                    {/* Keyed on the table's vote nonce: a new vote window is a
                        new vote (see DemoTableConfig.voteNonce). */}
                    <GameProvider key={tableConfigs[i].voteNonce}>
                      {/* GameInner's grid-cell column: felt (flex:1) over the
                          fixed 64px footer with the real action bar. */}
                      <div style={gridCellStyle}>
                        <div style={gridFeltStyle}>
                          <Table />
                        </div>
                        <div style={gridFooterStyle}>{actionBarFor(i)}</div>
                      </div>
                    </GameProvider>
                  </TableIdProvider>
                </OrientationOverrideProvider>
              </GridModeProvider>
            </GlobalProvider>
          </ShowAllDealersContext.Provider>
        </div>
      ))}
    </div>
  );

  return (
    <div style={pageStyle}>
      <button
        style={flyoutToggleStyle}
        onClick={() => setControlsOpen((v) => !v)}
        aria-expanded={controlsOpen}>
        {controlsOpen ? '✕' : '☰'} Controls
      </button>

      {controlsOpen && (
        <div style={flyoutPanelStyle}>
          <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Table Demo</div>

          <ControlSection
            title='Layout'
            // Shared by every open table.
            hint={tableCount > 1 ? 'All tables' : undefined}
            open={!collapsedSections['layout']}
            onToggle={() => toggleSection('layout')}>
            <div style={rowStyle}>
              <span style={labelStyle}>Tables</span>
              {Array.from({ length: MAX_OPEN_TABLES }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  onClick={() => setTableCount(n)}
                  style={{ ...seatButtonStyle, ...(tableCount === n ? activeButtonStyle : null) }}>
                  {n}
                </button>
              ))}
            </div>

            {tableCount > 1 && (
              <div style={rowStyle}>
                <span style={labelStyle}>Edit</span>
                {tableConfigs.map((_, i) => (
                  <button
                    key={i}
                    // Picks which table the controls below change (or click a table).
                    onClick={() => setEditIndex(i)}
                    style={{ ...seatButtonStyle, ...(editIndex === i ? activeButtonStyle : null) }}>
                    T{i + 1}
                  </button>
                ))}
                <button onClick={copyConfigToAllTables} style={smallButtonStyle}>
                  Copy to all
                </button>
              </div>
            )}

            <div style={rowStyle}>
              <span style={labelStyle}>View Size</span>
              <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>Small</span>
              <input
                type='range'
                aria-label='Table View Size'
                min={TABLE_VIEW_SCALE_MIN}
                max={TABLE_VIEW_SCALE_MAX}
                step={TABLE_VIEW_SCALE_STEP}
                value={tableSettings.tableViewScale}
                onChange={(e) => tableSettings.setTableViewScale(Number(e.target.value))}
                style={{ flex: 1, accentColor: '#E98C26', cursor: 'pointer', minWidth: 80 }}
              />
              <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>Largest</span>
              <span style={{ fontSize: '0.75rem', minWidth: 34, textAlign: 'right' }}>
                {Math.round(tableSettings.tableViewScale * 100)}%
              </span>
            </div>

            <div style={rowStyle}>
              <span style={labelStyle}>Dealers</span>
              <button
                onClick={() => setShowAllDealers((v) => !v)}
                style={{ ...buttonStyle, ...(showAllDealers ? activeButtonStyle : null) }}>
                All dealers
              </button>
            </div>
          </ControlSection>

          <ControlSection
            title='Table'
            // Edits the table picked under Layout → Edit.
            hint={editHint}
            open={!collapsedSections['table']}
            onToggle={() => toggleSection('table')}>
            <div style={rowStyle}>
              <span style={labelStyle}>Game</span>
              {GAME_VARIANTS.map(({ label, value }) => (
                <button
                  key={value}
                  onClick={() => setVariant(value)}
                  style={{ ...buttonStyle, ...(variant === value ? activeButtonStyle : null) }}>
                  {label}
                </button>
              ))}
            </div>

            <div style={rowStyle}>
              <span style={labelStyle}>Currency</span>
              {CURRENCIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCurrency(c)}
                  style={{ ...buttonStyle, ...(currency === c ? activeButtonStyle : null) }}>
                  {c}
                </button>
              ))}
            </div>

            <div style={rowStyle}>
              <span style={labelStyle}>Seats</span>
              {Array.from({ length: MAX_DEMO_SEATS }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  onClick={() => setSeatCount(n)}
                  style={{ ...seatButtonStyle, ...(seatCount === n ? activeButtonStyle : null) }}>
                  {n}
                </button>
              ))}
            </div>

            <div style={rowStyle}>
              <span style={labelStyle}>Open</span>
              {Array.from({ length: MAX_DEMO_SEATS }, (_, i) => i).map((k) => (
                <button
                  key={k}
                  // Leaves k seats empty (rendered as open seats); clamped so the
                  // POV always stays seated.
                  onClick={() => setOpenSeats(k)}
                  disabled={k > seatCount - 1}
                  style={{
                    ...seatButtonStyle,
                    ...(openSeats === k ? activeButtonStyle : null),
                    ...(k > seatCount - 1 ? { opacity: 0.3, cursor: 'not-allowed' } : null)
                  }}>
                  {k}
                </button>
              ))}
            </div>

            <div style={rowStyle}>
              <span style={labelStyle}>POV seat</span>
              <button
                // No POV: spectator view — no face-up cards, an unseated viewer,
                // and the chosen seat is vacated (rendered as an open seat).
                onClick={() => setPovAssigned(false)}
                style={{ ...buttonStyle, ...(!povAssigned ? activeButtonStyle : null) }}>
                None
              </button>
              {Array.from({ length: MAX_DEMO_SEATS }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  // The POV can sit on any seat at the table (it's always kept
                  // seated, open seats fill around it); only seats past the table
                  // size are unavailable. buildTableDemo clamps to match.
                  onClick={() => {
                    setPovSeat(n);
                    setPovAssigned(true);
                  }}
                  disabled={n > seatCount}
                  style={{
                    ...seatButtonStyle,
                    ...(povAssigned && Math.min(povSeat, seatCount) === n
                      ? activeButtonStyle
                      : null),
                    ...(n > seatCount ? { opacity: 0.3, cursor: 'not-allowed' } : null)
                  }}>
                  {n}
                </button>
              ))}
            </div>

            <div style={rowStyle}>
              <span style={labelStyle}>Bounty</span>
              <button
                // Toggle a Bounty game — shows the per-seat knockout badge.
                onClick={toggleBounty}
                style={{ ...buttonStyle, ...(bountyOn ? activeButtonStyle : null) }}>
                {bountyOn ? 'On' : 'Off'}
              </button>
            </div>
          </ControlSection>

          <ControlSection
            title='Hand'
            // Edits the table picked under Layout → Edit.
            hint={editHint}
            open={!collapsedSections['hand']}
            onToggle={() => toggleSection('hand')}>
            <div style={rowStyle}>
              <span style={labelStyle}>Board</span>
              {BOARD_STAGES.map((stage) => (
                <button
                  key={stage}
                  onClick={() => setBoardStage(stage)}
                  // Multi-run owns the community cards, so the stage comes from
                  // it rather than this control: the all-in street while the vote
                  // window is open, then each board run out to the river.
                  disabled={isMultiRun}
                  title={
                    isMultiRun
                      ? 'Driven by multi-run: the all-in street, then the run-out'
                      : undefined
                  }
                  style={{
                    ...buttonStyle,
                    ...(boardStage === stage && !isMultiRun ? activeButtonStyle : null),
                    ...(isMultiRun ? { opacity: 0.3, cursor: 'not-allowed' } : null)
                  }}>
                  {stage}
                </button>
              ))}
            </div>

            <div style={rowStyle}>
              <span style={labelStyle}>Turn</span>
              <button
                // Auto: the demo's default acting seat (an opponent), so the
                // action bar shows the POV's pre-actions.
                onClick={() => setTurnSeat(0)}
                style={{ ...buttonStyle, ...(turnSeat <= 0 ? activeButtonStyle : null) }}>
                Auto
              </button>
              {Array.from({ length: MAX_DEMO_SEATS }, (_, i) => i + 1).map((n) => {
                const disabled = !occupiedSet.has(n);
                const isPovSeat = povAssigned && n === pov;
                return (
                  <button
                    key={n}
                    // The POV's seat puts the live Fold / Call / Raise bar up.
                    onClick={() => setTurnSeat(n)}
                    disabled={disabled}
                    title={disabled ? undefined : isPovSeat ? 'POV to act' : `Seat ${n} to act`}
                    style={{
                      ...seatButtonStyle,
                      ...(turnSeat === n ? activeButtonStyle : null),
                      ...(isPovSeat && !disabled
                        ? { boxShadow: '0 0 0 2px rgba(212, 168, 83, 0.8)' }
                        : null),
                      ...(disabled ? { opacity: 0.3, cursor: 'not-allowed' } : null)
                    }}>
                    {n}
                  </button>
                );
              })}
            </div>

            <div style={rowStyle}>
              <span style={labelStyle}>Straddle</span>
              <button
                onClick={() => setStraddleSeat(0)}
                style={{ ...buttonStyle, ...(straddleSeat <= 0 ? activeButtonStyle : null) }}>
                None
              </button>
              {Array.from({ length: MAX_DEMO_SEATS }, (_, i) => i + 1).map((n) => {
                const disabled = !occupiedSet.has(n);
                return (
                  <button
                    key={n}
                    // Straddle only shows pre-flop, so assigning one jumps the board
                    // to pre-flop (like picking a side-pot count switches the pot).
                    onClick={() => {
                      setStraddleSeat(n);
                      setBoardStage('preflop');
                    }}
                    disabled={disabled}
                    style={{
                      ...seatButtonStyle,
                      ...(straddleSeat === n ? activeButtonStyle : null),
                      ...(disabled ? { opacity: 0.3, cursor: 'not-allowed' } : null)
                    }}>
                    {n}
                  </button>
                );
              })}
            </div>

            <div style={rowStyle}>
              <span style={labelStyle}>Pot</span>
              {POT_SCENARIOS.map((p) => (
                <button
                  key={p}
                  onClick={() => setPotScenario(p)}
                  style={{ ...buttonStyle, ...(potScenario === p ? activeButtonStyle : null) }}>
                  {p}
                </button>
              ))}
            </div>

            <div style={rowStyle}>
              <span style={labelStyle}>Side pots</span>
              {Array.from({ length: MAX_DEMO_SIDE_POTS }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  // Picking a count also switches to the side-pot scenario so it shows.
                  onClick={() => {
                    setSidePotCount(n);
                    setPotScenario('side');
                  }}
                  style={{
                    ...seatButtonStyle,
                    ...(potScenario === 'side' && sidePotCount === n ? activeButtonStyle : null)
                  }}>
                  {n}
                </button>
              ))}
            </div>
          </ControlSection>

          <ControlSection
            title='Multi-run'
            // Edits the table picked under Layout → Edit.
            hint={editHint}
            open={!collapsedSections['multiRun']}
            onToggle={() => toggleSection('multiRun')}>
            <div style={rowStyle}>
              <span style={labelStyle}>Multi-run</span>
              <button
                onClick={() => setMultiRunCount(0)}
                style={{ ...buttonStyle, ...(!isMultiRun ? activeButtonStyle : null) }}>
                Off
              </button>
              {MULTI_RUN_COUNTS.map((n) => (
                <button
                  key={n}
                  onClick={() => setMultiRunCount(n)}
                  style={{ ...buttonStyle, ...(multiRunCount === n ? activeButtonStyle : null) }}>
                  x{n}
                </button>
              ))}
            </div>

            {isMultiRun && (
              <>
                <div style={rowStyle}>
                  <span style={labelStyle}>All-in on</span>
                  {MULTI_RUN_TRIGGERS.map((trigger) => (
                    <button
                      key={trigger}
                      // The street the all-in happened on decides how much board
                      // every run shares: pre-flop none, post-flop the flop,
                      // post-turn the flop + turn.
                      onClick={() => setMultiRunTrigger(trigger)}
                      style={{
                        ...buttonStyle,
                        ...(multiRunTrigger === trigger ? activeButtonStyle : null)
                      }}>
                      {MULTI_RUN_TRIGGER_LABEL[trigger]}
                    </button>
                  ))}
                </div>

                <div style={rowStyle}>
                  <span style={labelStyle}>Run-out</span>
                  <button
                    // Before any board: the vote window is open and the real
                    // <MultiRunActions/> panel takes over the action bar. Clicking
                    // it again restarts the countdown.
                    onClick={openVotingWindow}
                    title='Vote window open — the all-in street is on the felt, no board run out yet'
                    style={{ ...buttonStyle, ...(isVoting ? activeButtonStyle : null) }}>
                    Voting
                  </button>
                  {Array.from({ length: multiRunCount }, (_, i) => i + 1).map((run) => (
                    <button
                      key={run}
                      // This run is the one being PAID; the boards before it have
                      // settled and show their winner, the ones after are already
                      // dealt and waiting their turn (all of them are drawn).
                      onClick={() => setMultiRunProgress(run)}
                      title={`Run ${run} on the felt — ${multiRunCount} boards shown, ${
                        run - 1
                      } settled`}
                      style={{
                        ...buttonStyle,
                        ...(multiRunProgress === run ? activeButtonStyle : null)
                      }}>
                      Run {run}
                    </button>
                  ))}
                  <button
                    // Every board dealt and paid out — the last state the client
                    // sits on before the hand resets.
                    onClick={() => setMultiRunProgress(multiRunCount + 1)}
                    style={{
                      ...buttonStyle,
                      ...(multiRunProgress > multiRunCount ? activeButtonStyle : null)
                    }}>
                    All paid
                  </button>
                </div>
              </>
            )}

            {/* EN-10856: the multi-run vote, one click per state. Needs a POV and
                two opponents; everyone else is folded while it is on. */}
            <div style={rowStyle}>
              <span style={labelStyle}>Vote</span>
              <button
                onClick={() => showVoteStep('off')}
                style={{ ...buttonStyle, ...(voteStep === 'off' ? activeButtonStyle : null) }}>
                Off
              </button>
              {VOTE_STEPS.map(({ step, label, title }) => (
                <button
                  key={step}
                  onClick={() =>
                    showVoteStep(step, step === 'declined' ? { declined: 'third-declined' } : {})
                  }
                  disabled={!voteCast}
                  title={voteCast ? title : 'Needs a POV seat and two opponents'}
                  style={{
                    ...buttonStyle,
                    textTransform: 'none',
                    ...(voteStep === step ? activeButtonStyle : null),
                    ...(!voteCast ? { opacity: 0.3, cursor: 'not-allowed' } : null)
                  }}>
                  {label}
                </button>
              ))}
            </div>

            <div style={rowStyle}>
              <span style={labelStyle}>Offer</span>
              {([2, 3] as VoteOffer[]).map((n) => (
                <button
                  key={n}
                  // The count the initiator put on the table (the text, the tint
                  // and the responder's second button all follow it).
                  onClick={() =>
                    voteOn
                      ? showVoteStep(voteStep, { offer: n })
                      : updateConfig(() => ({ voteOffer: n }))
                  }
                  style={{ ...buttonStyle, ...(voteOffer === n ? activeButtonStyle : null) }}>
                  {n}X
                </button>
              ))}
            </div>
          </ControlSection>

          <ControlSection
            title='Seats'
            // Edits the table picked under Layout → Edit.
            hint={editHint}
            open={!collapsedSections['seats']}
            onToggle={() => toggleSection('seats')}>
            <div style={rowStyle}>
              <span style={labelStyle}>Cards</span>
              {Array.from({ length: MAX_DEMO_SEATS }, (_, i) => i + 1).map((n) => {
                const isPovSeat = povAssigned && n === pov;
                // Only occupied seats are controllable; open seats have no cards.
                const disabled = !occupiedSet.has(n);
                const mode = resolveSeatCardMode(seatCardModes[n], isPovSeat);
                return (
                  <button
                    key={n}
                    // Tap to cycle. POV: shown → folded → none. Opponent: face-down
                    // → shown → folded → none.
                    onClick={() => cycleSeatCardMode(n, isPovSeat)}
                    disabled={disabled}
                    title={
                      disabled ? undefined : `${isPovSeat ? 'POV — ' : ''}${CARD_MODE_LABEL[mode]}`
                    }
                    style={{
                      ...seatButtonStyle,
                      ...(disabled
                        ? { opacity: 0.35, cursor: 'not-allowed' }
                        : {
                            background: CARD_MODE_COLOR[mode],
                            border: '1px solid rgba(255,255,255,0.25)',
                            // Gold ring marks the POV seat ("you").
                            ...(isPovSeat
                              ? { boxShadow: '0 0 0 2px rgba(212, 168, 83, 0.8)' }
                              : null)
                          })
                    }}>
                    {n}
                  </button>
                );
              })}
            </div>

            <div style={{ ...rowStyle, paddingLeft: 70, gap: '0.6rem' }}>
              {OPPONENT_CARD_CYCLE.map((m) => (
                <span key={m} style={legendItemStyle}>
                  <span style={{ ...legendSwatchStyle, backgroundColor: CARD_MODE_COLOR[m] }} />
                  {CARD_MODE_LABEL[m]}
                </span>
              ))}
            </div>

            <div style={rowStyle}>
              <span style={labelStyle}>Action</span>
              {Array.from({ length: MAX_DEMO_SEATS }, (_, i) => i + 1).map((n) => {
                const disabled = !occupiedSet.has(n);
                const action = seatActions[n] ?? 'none';
                // Folded seats always show FOLD (set via the Cards control).
                const folded = seatCardModes[n] === 'fold';
                const color = action === 'none' ? null : actionColors[action];
                return (
                  <button
                    key={n}
                    // Tap to cycle: none → check → call → bet → raise → all-in.
                    onClick={() => cycleSeatAction(n)}
                    disabled={disabled}
                    title={
                      disabled
                        ? undefined
                        : `Seat ${n}: ${folded ? 'fold (Cards control)' : action}`
                    }
                    style={{
                      ...seatButtonStyle,
                      ...(disabled
                        ? { opacity: 0.35, cursor: 'not-allowed' }
                        : color
                        ? { background: color.bg, border: `1px solid ${color.border}` }
                        : null),
                      ...(folded && !disabled ? { opacity: 0.5 } : null)
                    }}>
                    {disabled ? n : SEAT_ACTION_SHORT[action]}
                  </button>
                );
              })}
            </div>

            <div style={{ ...rowStyle, paddingLeft: 70 }}>
              {SEAT_ACTION_CYCLE.map((a) => (
                <button
                  key={a}
                  // Apply one action to every occupied seat at once.
                  onClick={() => setAllSeatActions(a)}
                  style={{
                    ...smallButtonStyle,
                    ...(a === 'none' ? null : { borderColor: actionColors[a].border })
                  }}>
                  {a === 'none' ? 'Clear' : a === 'allin' ? 'All in' : a}
                </button>
              ))}
            </div>

            <div style={rowStyle}>
              <span style={labelStyle}>Bet</span>
              {DEMO_BET_AMOUNTS.map((amount) => (
                <button
                  key={amount}
                  // Every active seat's chips in front — previews how wide the
                  // bet pill grows. Check / fold still put no chips in.
                  onClick={() => setBetAmount(amount)}
                  style={{
                    ...buttonStyle,
                    ...(betAmount === amount ? activeButtonStyle : null)
                  }}>
                  {formatBetPreset(amount)}
                </button>
              ))}
            </div>

            {bountyOn && (
              <div style={rowStyle}>
                <span style={labelStyle}>Knockouts</span>
                {Array.from({ length: MAX_DEMO_SEATS }, (_, i) => i + 1).map((n) => {
                  const disabled = !occupiedSet.has(n);
                  const knockouts = seatKnockouts[n] ?? DEFAULT_SEAT_KNOCKOUTS;
                  return (
                    <button
                      key={n}
                      // Tap to cycle this seat's knockout count (0 = no badge).
                      onClick={() => cycleSeatKnockouts(n)}
                      disabled={disabled}
                      title={disabled ? undefined : `Seat ${n}: ${knockouts} knockouts`}
                      style={{
                        ...seatButtonStyle,
                        ...(disabled
                          ? { opacity: 0.35, cursor: 'not-allowed' }
                          : knockouts > 0
                          ? {
                              background: 'rgba(212, 168, 83, 0.3)',
                              border: '1px solid rgba(212, 168, 83, 0.7)'
                            }
                          : null)
                      }}>
                      {knockouts}
                    </button>
                  );
                })}
              </div>
            )}
          </ControlSection>
        </div>
      )}

      {/* Header/footer take exactly the space the real chrome does, so each
          felt gets the same box as in-game. */}
      {showGrid ? (
        <div style={stageStyle}>
          {/* Grid: the header is IN FLOW above the row (MultiTableGrid). */}
          <div style={gridPageStyle}>
            <div style={gridHeaderPlaceholderStyle}>header</div>
            {multiTable}
          </div>
        </div>
      ) : (
        // Single table, scoped like the real panel (Game → TableIdProvider →
        // GameProvider keyed per table), so the felt and its action bar share
        // one GameContext (pre-selected actions, the vote cast) and reset on a
        // table switch or a new vote window.
        <ShowAllDealersContext.Provider value={showAllDealers}>
          <TableIdProvider gameID={editGameID}>
            <GameProvider key={`${editGameID}:${editConfig.voteNonce}`}>
              <div style={stageStyle}>
                {/* The header is an overlay, but its band is reserved as felt
                    padding (see singleHeader). */}
                {singleHeader}
                {/* Absolute-fill wrapper gives the table a definite size so its
                    height:100% resolves (a plain flex child collapses here). */}
                <div style={singleTableFillStyle}>
                  <Table />
                </div>
              </div>
              {/* The real action bar in the real footer row: it sets the
                  in-game height (content-sized — it follows what the POV can
                  do right now). Locked, except for the vote panel. */}
              <div style={footerRow}>{actionBarFor(editIndex)}</div>
            </GameProvider>
          </TableIdProvider>
        </ShowAllDealersContext.Provider>
      )}
    </div>
  );
};

export default DemoTable;

// One control section: a header button over its rows, divided by a hairline.
const sectionStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  paddingTop: '0.4rem',
  borderTop: '1px solid rgba(212, 168, 83, 0.15)'
};

const sectionHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.35rem',
  padding: '0.1rem 0',
  border: 0,
  background: 'transparent',
  color: 'rgba(212, 168, 83, 0.9)',
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  textAlign: 'left'
};

const sectionHintStyle: CSSProperties = {
  marginLeft: 'auto',
  fontWeight: 400,
  letterSpacing: 0,
  textTransform: 'none',
  color: 'rgba(255, 255, 255, 0.5)'
};

const sectionBodyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.4rem',
  padding: '0.35rem 0 0.25rem'
};

const lockedActionBarStyle: CSSProperties = {
  display: 'contents',
  pointerEvents: 'none'
};

const pageStyle: CSSProperties = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  width: '100vw',
  background: 'linear-gradient(180deg, #1c1a14 0%, #12100c 100%)',
  color: 'white',
  overflow: 'hidden'
};

// Toggle pinned top-left; overlays the stage so it never shrinks the table.
const flyoutToggleStyle: CSSProperties = {
  position: 'absolute',
  top: 10,
  left: 10,
  zIndex: 1001,
  padding: '0.25rem 0.6rem',
  borderRadius: 6,
  border: '1px solid rgba(212, 168, 83, 0.6)',
  background: 'rgba(18, 16, 12, 0.9)',
  color: 'white',
  cursor: 'pointer',
  fontWeight: 600
};

// The controls panel — floats over the stage just below the toggle.
const flyoutPanelStyle: CSSProperties = {
  position: 'absolute',
  top: 48,
  left: 10,
  zIndex: 1000,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.4rem',
  padding: '0.6rem 0.75rem',
  maxHeight: 'calc(100vh - 64px)',
  overflowY: 'auto',
  borderRadius: 8,
  border: '1px solid rgba(212, 168, 83, 0.25)',
  background: 'rgba(18, 16, 12, 0.95)',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)'
};

// One control group: a fixed-width label followed by its buttons.
const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '0.35rem'
};

const labelStyle: CSSProperties = {
  width: 70,
  flexShrink: 0,
  opacity: 0.7,
  fontSize: '0.85rem'
};

// One entry in the Cards legend: a colour swatch + its mode label.
const legendItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.3rem',
  fontSize: '0.7rem',
  opacity: 0.8
};

const legendSwatchStyle: CSSProperties = {
  width: 10,
  height: 10,
  borderRadius: 2,
  display: 'inline-block'
};

const stageStyle: CSSProperties = {
  flex: 1,
  position: 'relative',
  overflow: 'hidden'
};

// Fills the stage below the reserved header band with a definite size (absolute),
// so the table's height:100% resolves — matching the real tableRow, whose top
// padding is the same measured chrome inset.
const singleTableFillStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  top: `var(${CHROME_INSET_VAR}, 0px)`
};

// Strip band geometry, copied from TableHeader's saloonOverlayStripBand /
// saloonPortraitStripBand (12px top clearance; edges kept clear of the corner
// clusters — the portrait value is the band's fallback inset). Only its height
// feeds the felt; the placement just keeps the strip where it sits in-game.
const stripBandStyle: CSSProperties = {
  position: 'absolute',
  top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
  left: 76,
  right: 76,
  zIndex: 1
};

const stripInsetPortrait: CSSProperties = { left: 56, right: 56 };

// Stacked "{N} TABLES" badge band (TableHeader saloonPortraitCenter).
const stackedBadgeBandStyle: CSSProperties = {
  position: 'absolute',
  top: 'calc(env(safe-area-inset-top, 0px) + 20px)',
  left: 0,
  right: 0,
  display: 'flex',
  justifyContent: 'center',
  zIndex: 1
};

// Faint uppercase label shared by every chrome placeholder.
const placeholderLabelStyle: CSSProperties = {
  fontSize: '0.7rem',
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.25)'
};

// Placeholders matching the real game's chrome so the table gets the same box
// as in-game.
const chromeBarStyle: CSSProperties = {
  ...placeholderLabelStyle,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  background: '#12100c'
};

// The single-table dock band the felt reserves (TableHeader
// saloonOverlayDockBand → useChromeInset).
const singleHeaderPlaceholderStyle: CSSProperties = {
  ...chromeBarStyle,
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: COMPACT_HEADER_MIN_HEIGHT,
  background: 'transparent',
  borderBottom: '1px dashed rgba(212, 168, 83, 0.12)'
};

// MultiTableGrid's column: header bar, then the carousel row.
const gridPageStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column'
};

// The grid's in-flow compact header: a COMPACT_HEADER_MIN_HEIGHT row plus the
// wrapper's 1px bottom border (TableHeader saloonHeaderWrapper).
const gridHeaderPlaceholderStyle: CSSProperties = {
  ...chromeBarStyle,
  height: COMPACT_HEADER_MIN_HEIGHT + 1,
  borderBottom: '1px solid rgba(212, 168, 83, 0.18)'
};

const buttonStyle: CSSProperties = {
  padding: '0.25rem 0.75rem',
  borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.2)',
  background: 'transparent',
  color: 'white',
  cursor: 'pointer',
  textTransform: 'capitalize'
};

// Compact button for the "apply to all seats" shortcuts.
const smallButtonStyle: CSSProperties = {
  ...buttonStyle,
  padding: '0.1rem 0.45rem',
  fontSize: '0.7rem'
};

const seatButtonStyle: CSSProperties = {
  width: 30,
  padding: '0.25rem 0',
  borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.2)',
  background: 'transparent',
  color: 'white',
  cursor: 'pointer',
  textAlign: 'center'
};

const activeButtonStyle: CSSProperties = {
  background: 'rgba(212, 168, 83, 0.25)',
  // Full shorthand (not borderColor) so it doesn't conflict with the `border`
  // shorthand on the base button styles when toggled on/off.
  border: '1px solid rgba(212, 168, 83, 0.6)'
};

// Marks the table the controls are editing in the multi-table row.
const editOutline = '1px dashed rgba(212, 168, 83, 0.6)';
